import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS, waLink, defaultWaMessage } from "@/lib/config";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Products", href: "#products" },
  { label: "Brands", href: "#brands" },
  { label: "Why Us", href: "#why-us" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#00296B]/10 shadow-[0_2px_20px_-10px_rgba(0,41,107,0.25)]"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <a
          href="#home"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <div className="w-11 h-11 bg-white border border-[#00296B]/10 flex items-center justify-center overflow-hidden">
            <img
              src={BUSINESS.logoUrl}
              alt="Master Mall Nashik logo"
              className="w-full h-full object-contain p-1"
            />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[#00296B] text-base sm:text-lg">
              Master Mall
            </div>
            <div className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#334155] font-medium">
              Nashik
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[#00296B] font-medium text-sm relative py-1 hover:text-[#00296B] transition-colors group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-0.5 w-0 h-0.5 bg-[#FFD500] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            data-testid="nav-call-btn"
            className="inline-flex items-center gap-2 text-[#00296B] font-semibold text-sm"
          >
            <Phone className="w-4 h-4" />
            {BUSINESS.phoneDisplay}
          </a>
          <a
            href={waLink(defaultWaMessage)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-btn"
            className="inline-flex items-center gap-2 bg-[#FFD500] text-[#00296B] font-semibold px-5 py-2.5 hover:bg-[#E6C000] transition-colors"
          >
            Enquire Now
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 text-[#00296B]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-white border-t border-[#00296B]/10 px-4 py-4 flex flex-col gap-1"
        >
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className="py-3 px-2 text-[#00296B] font-medium border-b border-[#00296B]/5"
            >
              {l.label}
            </a>
          ))}
          <div className="flex gap-2 mt-3">
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="nav-mobile-call"
              className="flex-1 inline-flex items-center justify-center gap-2 border border-[#00296B] text-[#00296B] font-semibold px-4 py-3"
            >
              <Phone className="w-4 h-4" /> Call
            </a>
            <a
              href={waLink(defaultWaMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-whatsapp"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FFD500] text-[#00296B] font-semibold px-4 py-3"
            >
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
